// =============================================================================
// AdminBroadcastsPage — compose & manage announcements (email / banner / inbox)
// =============================================================================
// Platform-admin surface for one-to-many announcements. A broadcast can target
// all orgs, specific orgs, a plan, a role, or specific users, and fan out over
// any combination of in-app inbox, dashboard banner, and branded email. Heavy
// fan-out runs in a worker — "Send now" only flips the status + enqueues a job.
//
// Guarded upstream by the `notification:*` route guard; mutating controls are
// hidden when the admin lacks `notification:manage`.
// =============================================================================

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BroadcastAudienceType,
  BroadcastStatus,
  NotificationCategory,
  hasPermission,
} from '@tkpod/shared';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  Modal,
  ModalFooter,
  Select,
  Spinner,
  useConfirm,
  type BadgeVariant,
} from '@tkpod/ui';

import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { toast } from '@/lib/toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BroadcastAudience {
  type: BroadcastAudienceType;
  organizationIds?: string[];
  planIds?: string[];
  roleSlugs?: string[];
  userIds?: string[];
}

interface Broadcast {
  id: string;
  title: string;
  bodyHtml: string;
  category: NotificationCategory;
  emailEnabled: boolean;
  bannerEnabled: boolean;
  inboxEnabled: boolean;
  bannerTone: string | null;
  audienceType: BroadcastAudienceType;
  audience: BroadcastAudience | null;
  notificationType: string;
  status: BroadcastStatus;
  scheduledAt: string | null;
  expiresAt: string | null;
  sentAt: string | null;
  recipientCount: number;
  inboxSentCount: number;
  sentCount: number;
  failedCount: number;
  createdAt: string;
}

interface ListResponse {
  items: Broadcast[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

interface RenderedEmail {
  subject: string;
  html: string;
  text: string;
}

interface FormState {
  title: string;
  bodyHtml: string;
  category: NotificationCategory;
  emailEnabled: boolean;
  bannerEnabled: boolean;
  inboxEnabled: boolean;
  bannerTone: string;
  audienceType: BroadcastAudienceType;
  audienceValue: string; // comma-separated ids/slugs for the conditional input
  scheduledAt: string;
  expiresAt: string;
}

type ModalMode = 'create' | 'edit' | null;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMPTY_FORM: FormState = {
  title: '',
  bodyHtml: '',
  category: NotificationCategory.TRANSACTIONAL,
  emailEnabled: false,
  bannerEnabled: false,
  inboxEnabled: true,
  bannerTone: 'info',
  audienceType: BroadcastAudienceType.ALL_ORGANIZATIONS,
  audienceValue: '',
  scheduledAt: '',
  expiresAt: '',
};

const CATEGORY_OPTIONS = [
  { value: NotificationCategory.TRANSACTIONAL, label: 'Transactional (always delivered)' },
  { value: NotificationCategory.MARKETING, label: 'Marketing (respects unsubscribe)' },
];

const AUDIENCE_OPTIONS = [
  { value: BroadcastAudienceType.ALL_ORGANIZATIONS, label: 'All organizations' },
  { value: BroadcastAudienceType.SPECIFIC_ORGANIZATIONS, label: 'Specific organizations' },
  { value: BroadcastAudienceType.BY_PLAN, label: 'By plan' },
  { value: BroadcastAudienceType.BY_ROLE, label: 'By role' },
  { value: BroadcastAudienceType.SPECIFIC_USERS, label: 'Specific users' },
];

const BANNER_TONE_OPTIONS = [
  { value: 'info', label: 'Info (blue)' },
  { value: 'success', label: 'Success (green)' },
  { value: 'warning', label: 'Warning (amber)' },
  { value: 'danger', label: 'Danger (red)' },
];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: BroadcastStatus.DRAFT, label: 'Draft' },
  { value: BroadcastStatus.SCHEDULED, label: 'Scheduled' },
  { value: BroadcastStatus.SENDING, label: 'Sending' },
  { value: BroadcastStatus.SENT, label: 'Sent' },
  { value: BroadcastStatus.CANCELLED, label: 'Cancelled' },
];

const STATUS_BADGE: Record<BroadcastStatus, BadgeVariant> = {
  DRAFT: 'default',
  SCHEDULED: 'info',
  SENDING: 'warning',
  SENT: 'success',
  CANCELLED: 'default',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const queryKeys = {
  broadcasts: (status: string, page: number) =>
    ['admin', 'broadcasts', { status, page }] as const,
};

/** Which conditional id/slug field a given audience type drives. */
function audienceField(
  type: BroadcastAudienceType,
): 'organizationIds' | 'planIds' | 'roleSlugs' | 'userIds' | null {
  switch (type) {
    case BroadcastAudienceType.SPECIFIC_ORGANIZATIONS:
      return 'organizationIds';
    case BroadcastAudienceType.BY_PLAN:
      return 'planIds';
    case BroadcastAudienceType.BY_ROLE:
      return 'roleSlugs';
    case BroadcastAudienceType.SPECIFIC_USERS:
      return 'userIds';
    default:
      return null;
  }
}

function audienceLabel(type: BroadcastAudienceType): string {
  return (
    AUDIENCE_OPTIONS.find((o) => o.value === type)?.label ?? String(type)
  );
}

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Map an existing broadcast back into the editable form shape. */
function broadcastToForm(b: Broadcast): FormState {
  const field = audienceField(b.audienceType);
  const csv =
    field && b.audience?.[field] ? (b.audience[field] as string[]).join(', ') : '';
  return {
    title: b.title,
    bodyHtml: b.bodyHtml,
    category: b.category,
    emailEnabled: b.emailEnabled,
    bannerEnabled: b.bannerEnabled,
    inboxEnabled: b.inboxEnabled,
    bannerTone: b.bannerTone ?? 'info',
    audienceType: b.audienceType,
    audienceValue: csv,
    scheduledAt: toLocalInput(b.scheduledAt),
    expiresAt: toLocalInput(b.expiresAt),
  };
}

/** ISO string → value usable in <input type="datetime-local"> (local tz). */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

/** datetime-local value → ISO 8601 (or undefined when blank). */
function fromLocalInput(value: string): string | undefined {
  if (!value.trim()) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** Build the create/update request payload from form state. */
function buildPayload(form: FormState): Record<string, unknown> {
  const field = audienceField(form.audienceType);
  const audience: BroadcastAudience = { type: form.audienceType };
  if (field) audience[field] = splitCsv(form.audienceValue);

  return {
    title: form.title.trim(),
    bodyHtml: form.bodyHtml,
    category: form.category,
    emailEnabled: form.emailEnabled,
    bannerEnabled: form.bannerEnabled,
    inboxEnabled: form.inboxEnabled,
    bannerTone: form.bannerEnabled ? form.bannerTone : undefined,
    audience,
    scheduledAt: fromLocalInput(form.scheduledAt),
    expiresAt: fromLocalInput(form.expiresAt),
  };
}

/** Compact button for the body formatting toolbar. */
function BodyToolbarButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="inline-flex h-7 min-w-7 items-center justify-center rounded px-2 text-xs text-text-secondary transition-colors hover:bg-surface hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30"
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdminBroadcastsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const permissions = user?.permissions ?? [];
  const canManage = hasPermission(permissions, 'notification:manage');

  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<RenderedEmail | null>(null);
  // Test-send modal (themed replacement for the native prompt).
  const [testTarget, setTestTarget] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testError, setTestError] = useState<string | null>(null);

  const listQuery = useQuery<ListResponse, Error>({
    queryKey: queryKeys.broadcasts(statusFilter, page),
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter) params.set('status', statusFilter);
      return api.get<ListResponse>(
        `/api/v1/admin/notifications/broadcasts?${params.toString()}`,
      );
    },
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'broadcasts'] });
  }, [queryClient]);

  // --- Mutations -----------------------------------------------------------

  const saveMutation = useMutation<Broadcast, Error, void>({
    mutationFn: () => {
      const payload = buildPayload(form);
      return modalMode === 'edit' && editId
        ? api.patch<Broadcast>(
            `/api/v1/admin/notifications/broadcasts/${editId}`,
            payload,
          )
        : api.post<Broadcast>('/api/v1/admin/notifications/broadcasts', payload);
    },
    onSuccess: () => {
      invalidate();
      setModalMode(null);
      toast(
        modalMode === 'edit' ? 'Announcement updated.' : 'Announcement created.',
      );
    },
    onError: (err) => setFormError(err.message),
  });

  const sendMutation = useMutation<unknown, Error, string>({
    mutationFn: (id) =>
      api.post(`/api/v1/admin/notifications/broadcasts/${id}/send`, {}),
    onSuccess: () => {
      invalidate();
      toast('Announcement queued for delivery.');
    },
    onError: (err) => toast(err.message, 'error'),
  });

  const cancelMutation = useMutation<unknown, Error, string>({
    mutationFn: (id) =>
      api.post(`/api/v1/admin/notifications/broadcasts/${id}/cancel`, {}),
    onSuccess: () => {
      invalidate();
      toast('Announcement cancelled.');
    },
    onError: (err) => toast(err.message, 'error'),
  });

  const deleteMutation = useMutation<unknown, Error, string>({
    mutationFn: (id) =>
      api.delete(`/api/v1/admin/notifications/broadcasts/${id}`),
    onSuccess: () => {
      invalidate();
      toast('Announcement deleted.');
    },
    onError: (err) => toast(err.message, 'error'),
  });

  const testMutation = useMutation<unknown, Error, { id: string; email: string }>(
    {
      mutationFn: ({ id, email }) =>
        api.post(`/api/v1/admin/notifications/broadcasts/${id}/test`, { email }),
      onSuccess: () => toast('Test email queued.'),
      onError: (err) => toast(err.message, 'error'),
    },
  );

  const previewMutation = useMutation<RenderedEmail, Error, void>({
    mutationFn: () =>
      api.post<RenderedEmail>('/api/v1/admin/notifications/preview', {
        title: form.title.trim() || '(untitled)',
        bodyHtml: form.bodyHtml,
        category: form.category,
      }),
    onSuccess: (data) => setPreviewHtml(data),
    onError: (err) => toast(err.message, 'error'),
  });

  // --- Handlers ------------------------------------------------------------

  const openCreate = useCallback(() => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalMode('create');
  }, []);

  const openEdit = useCallback((b: Broadcast) => {
    setEditId(b.id);
    setForm(broadcastToForm(b));
    setFormError(null);
    setModalMode('edit');
  }, []);

  const closeModal = useCallback(() => {
    setModalMode(null);
    setEditId(null);
    setFormError(null);
    saveMutation.reset();
  }, [saveMutation]);

  const field = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // --- Body formatting toolbar ---------------------------------------------
  // A lightweight rich-text helper: the buttons splice allow-listed HTML into
  // the textarea around the current selection. No editor dependency, and every
  // tag it emits matches the server-side sanitize allow-list 1:1.
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const editBody = useCallback(
    (
      transform: (sel: { value: string; start: number; end: number }) => {
        value: string;
        caretStart: number;
        caretEnd: number;
      },
    ) => {
      const el = bodyRef.current;
      if (!el) return;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const next = transform({ value: el.value, start, end });
      field('bodyHtml', next.value);
      // Restore focus + caret after React commits the new value.
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(next.caretStart, next.caretEnd);
      });
    },
    [field],
  );

  const wrapSelection = useCallback(
    (open: string, close: string) =>
      editBody(({ value, start, end }) => {
        const selected = value.slice(start, end);
        const insert = `${open}${selected}${close}`;
        const nextValue = value.slice(0, start) + insert + value.slice(end);
        // No selection → drop the caret between the tags so the admin can type.
        const caret = selected ? start + insert.length : start + open.length;
        return { value: nextValue, caretStart: caret, caretEnd: caret };
      }),
    [editBody],
  );

  const insertList = useCallback(
    (ordered: boolean) =>
      editBody(({ value, start, end }) => {
        const selected = value.slice(start, end).trim();
        const items = selected ? selected.split('\n') : ['First item', 'Second item'];
        const tag = ordered ? 'ol' : 'ul';
        const lis = items.map((i) => `  <li>${i.trim() || 'Item'}</li>`).join('\n');
        const block = `<${tag}>\n${lis}\n</${tag}>`;
        const before = value.slice(0, start);
        const prefix = before && !before.endsWith('\n') ? '\n' : '';
        const insert = `${prefix}${block}`;
        const nextValue = before + insert + value.slice(end);
        const caret = before.length + insert.length;
        return { value: nextValue, caretStart: caret, caretEnd: caret };
      }),
    [editBody],
  );

  const insertLink = useCallback(() => {
    // Insert an editable anchor template at the cursor (no popup). The admin
    // types the real URL over the pre-selected `https://` placeholder; the
    // server sanitizer drops any non-http(s)/mailto scheme on save.
    const PLACEHOLDER = 'https://';
    editBody(({ value, start, end }) => {
      const label = value.slice(start, end) || 'link text';
      const insert = `<a href="${PLACEHOLDER}">${label}</a>`;
      const nextValue = value.slice(0, start) + insert + value.slice(end);
      // Pre-select the placeholder URL so the admin can immediately overwrite it.
      const hrefStart = start + insert.indexOf(PLACEHOLDER);
      return {
        value: nextValue,
        caretStart: hrefStart,
        caretEnd: hrefStart + PLACEHOLDER.length,
      };
    });
  }, [editBody]);

  const handleSubmit = useCallback(() => {
    setFormError(null);
    if (!form.title.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!form.bodyHtml.trim()) {
      setFormError('Body is required.');
      return;
    }
    if (!form.emailEnabled && !form.bannerEnabled && !form.inboxEnabled) {
      setFormError('Enable at least one channel (email, banner, or inbox).');
      return;
    }
    if (audienceField(form.audienceType) && !splitCsv(form.audienceValue).length) {
      setFormError(
        `"${audienceLabel(form.audienceType)}" requires at least one id/slug.`,
      );
      return;
    }
    saveMutation.mutate();
  }, [form, saveMutation]);

  const openTest = useCallback((id: string) => {
    setTestTarget(id);
    setTestEmail('');
    setTestError(null);
  }, []);

  const confirmTest = useCallback(() => {
    const email = testEmail.trim();
    if (!email.includes('@')) {
      setTestError('Please enter a valid email address.');
      return;
    }
    if (testTarget) testMutation.mutate({ id: testTarget, email });
    setTestTarget(null);
  }, [testEmail, testTarget, testMutation]);

  // --- Render --------------------------------------------------------------

  const broadcasts = listQuery.data?.items ?? [];
  const meta = listQuery.data?.meta;
  const conditionalField = audienceField(form.audienceType);

  if (listQuery.isLoading && !listQuery.data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading announcements..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {confirmDialog}
      <Card>
        <CardHeader
          title="Announcements"
          description="Compose announcements delivered over in-app inbox, dashboard banners, and branded email. Fan-out runs in the background."
          action={
            <div className="flex items-center gap-3">
              <Select
                aria-label="Status filter"
                options={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="w-44"
              />
              {canManage && <Button onClick={openCreate}>New announcement</Button>}
            </div>
          }
        />

        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          {broadcasts.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
              No announcements found.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <Th>Title</Th>
                  <Th>Channels</Th>
                  <Th>Audience</Th>
                  <Th>Status</Th>
                  <Th>Delivery</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {broadcasts.map((b) => (
                  <BroadcastRow
                    key={b.id}
                    broadcast={b}
                    canManage={canManage}
                    busy={
                      sendMutation.isPending ||
                      cancelMutation.isPending ||
                      deleteMutation.isPending
                    }
                    onEdit={() => openEdit(b)}
                    onSend={async () => {
                      const ok = await confirm({
                        title: 'Send announcement now',
                        description: `"${b.title}" will be fanned out to all matching recipients. This cannot be undone.`,
                        confirmLabel: 'Send now',
                      });
                      if (ok) sendMutation.mutate(b.id);
                    }}
                    onCancel={() => cancelMutation.mutate(b.id)}
                    onDelete={async () => {
                      const ok = await confirm({
                        title: 'Delete announcement',
                        description:
                          b.status === BroadcastStatus.SENT
                            ? `"${b.title}" will be deleted and recalled — already-delivered inbox notifications and email records will be removed.`
                            : `"${b.title}" will be permanently deleted.`,
                        confirmLabel: 'Delete',
                        tone: 'danger',
                      });
                      if (ok) deleteMutation.mutate(b.id);
                    }}
                    onTest={() => openTest(b.id)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {meta && meta.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm text-text-secondary">
            <span>
              Page {meta.page} of {meta.totalPages} · {meta.total} total
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={meta.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={meta.page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Compose / edit modal */}
      <Modal
        open={modalMode !== null}
        onClose={closeModal}
        title={modalMode === 'edit' ? 'Edit announcement' : 'New announcement'}
        size="2xl"
      >
        {modalMode !== null && (
          <div className="space-y-4">
            {formError && (
              <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
                {formError}
              </div>
            )}

            <Input
              label="Title"
              value={form.title}
              onChange={(e) => field('title', e.target.value)}
              maxLength={200}
            />

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Body (HTML allowed — sanitized on save)
              </label>
              <div className="flex flex-wrap items-center gap-1 rounded-t-md border border-b-0 border-border bg-surface-secondary px-2 py-1.5">
                <BodyToolbarButton
                  label="Bold"
                  onClick={() => wrapSelection('<strong>', '</strong>')}
                >
                  <span className="font-bold">B</span>
                </BodyToolbarButton>
                <BodyToolbarButton
                  label="Italic"
                  onClick={() => wrapSelection('<em>', '</em>')}
                >
                  <span className="italic">I</span>
                </BodyToolbarButton>
                <BodyToolbarButton
                  label="Heading"
                  onClick={() => wrapSelection('<h3>', '</h3>')}
                >
                  <span className="font-semibold">H</span>
                </BodyToolbarButton>
                <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
                <BodyToolbarButton label="Insert link" onClick={insertLink}>
                  Link
                </BodyToolbarButton>
                <BodyToolbarButton
                  label="Bullet list"
                  onClick={() => insertList(false)}
                >
                  • List
                </BodyToolbarButton>
                <BodyToolbarButton
                  label="Numbered list"
                  onClick={() => insertList(true)}
                >
                  1. List
                </BodyToolbarButton>
                <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
                <BodyToolbarButton
                  label="Paragraph"
                  onClick={() => wrapSelection('<p>', '</p>')}
                >
                  ¶
                </BodyToolbarButton>
              </div>
              <textarea
                ref={bodyRef}
                value={form.bodyHtml}
                onChange={(e) => field('bodyHtml', e.target.value)}
                rows={6}
                maxLength={50_000}
                className="block w-full rounded-b-md border border-border bg-surface px-3 py-2 font-mono text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                placeholder="<p>Hello! We just shipped a new feature…</p>"
              />
              <p className="mt-1 text-xs text-text-tertiary">
                Select text then click a button to wrap it, or insert at the
                cursor. A safe subset of HTML is allowed (paragraphs, links,
                lists, emphasis); scripts and styles are stripped. Use “Preview
                email” to confirm the result.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                label="Category"
                options={CATEGORY_OPTIONS}
                value={form.category}
                onChange={(e) =>
                  field('category', e.target.value as NotificationCategory)
                }
                hint="Marketing emails honour the recipient's unsubscribe choice."
              />
              <Select
                label="Audience"
                options={AUDIENCE_OPTIONS}
                value={form.audienceType}
                onChange={(e) =>
                  field('audienceType', e.target.value as BroadcastAudienceType)
                }
              />
            </div>

            {conditionalField && (
              <Input
                label={
                  conditionalField === 'roleSlugs'
                    ? 'Role slugs (comma-separated)'
                    : conditionalField === 'planIds'
                      ? 'Plan IDs (comma-separated)'
                      : conditionalField === 'userIds'
                        ? 'User IDs (comma-separated)'
                        : 'Organization IDs (comma-separated)'
                }
                value={form.audienceValue}
                onChange={(e) => field('audienceValue', e.target.value)}
                placeholder={
                  conditionalField === 'roleSlugs'
                    ? 'owner, admin'
                    : 'id-1, id-2, id-3'
                }
              />
            )}

            {/* Channels */}
            <div className="rounded-md border border-border bg-surface-secondary px-4 py-3">
              <p className="text-sm font-medium text-text-primary mb-2">
                Channels
              </p>
              <div className="flex flex-wrap gap-4">
                <Toggle
                  label="In-app inbox"
                  checked={form.inboxEnabled}
                  onChange={(v) => field('inboxEnabled', v)}
                />
                <Toggle
                  label="Dashboard banner"
                  checked={form.bannerEnabled}
                  onChange={(v) => field('bannerEnabled', v)}
                />
                <Toggle
                  label="Email"
                  checked={form.emailEnabled}
                  onChange={(v) => field('emailEnabled', v)}
                />
              </div>
            </div>

            {form.bannerEnabled && (
              <Select
                label="Banner tone"
                options={BANNER_TONE_OPTIONS}
                value={form.bannerTone}
                onChange={(e) => field('bannerTone', e.target.value)}
                className="sm:w-60"
              />
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Schedule (optional)
                </label>
                <input
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={(e) => field('scheduledAt', e.target.value)}
                  className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1.5">
                  Banner expires (optional)
                </label>
                <input
                  type="datetime-local"
                  value={form.expiresAt}
                  onChange={(e) => field('expiresAt', e.target.value)}
                  className="block w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                />
              </div>
            </div>

            <ModalFooter>
              <Button
                variant="ghost"
                onClick={() => previewMutation.mutate()}
                loading={previewMutation.isPending}
                disabled={!form.bodyHtml.trim()}
              >
                Preview email
              </Button>
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} loading={saveMutation.isPending}>
                {modalMode === 'edit' ? 'Save changes' : 'Create draft'}
              </Button>
            </ModalFooter>
          </div>
        )}
      </Modal>

      {/* Email preview modal */}
      <Modal
        open={previewHtml !== null}
        onClose={() => setPreviewHtml(null)}
        title="Email preview"
        size="2xl"
      >
        {previewHtml && (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">
              <span className="font-medium text-text-primary">Subject:</span>{' '}
              {previewHtml.subject}
            </p>
            <iframe
              title="Email preview"
              srcDoc={previewHtml.html}
              sandbox=""
              className="h-[28rem] w-full rounded-md border border-border bg-white"
            />
            <ModalFooter>
              <Button variant="outline" onClick={() => setPreviewHtml(null)}>
                Close
              </Button>
            </ModalFooter>
          </div>
        )}
      </Modal>

      {/* Test-send modal (themed replacement for the native prompt) */}
      <Modal
        open={testTarget !== null}
        onClose={() => setTestTarget(null)}
        title="Send test email"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Send a one-off test of this announcement's email to a single address.
            This does not affect the announcement's audience or delivery counts.
          </p>
          <Input
            label="Recipient email"
            type="email"
            value={testEmail}
            onChange={(e) => {
              setTestEmail(e.target.value);
              if (testError) setTestError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmTest();
              }
            }}
            placeholder="you@example.com"
            error={testError ?? undefined}
            autoFocus
          />
          <ModalFooter>
            <Button variant="outline" onClick={() => setTestTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={confirmTest}
              loading={testMutation.isPending}
              disabled={!testEmail.trim()}
            >
              Send test
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Th({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary ${className ?? ''}`}
    >
      {children}
    </th>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-text-primary">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
          checked ? 'bg-brand-600' : 'bg-border'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
      {label}
    </label>
  );
}

function ChannelChips({ broadcast }: { broadcast: Broadcast }) {
  const chips: string[] = [];
  if (broadcast.inboxEnabled) chips.push('Inbox');
  if (broadcast.bannerEnabled) chips.push('Banner');
  if (broadcast.emailEnabled) chips.push('Email');
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((c) => (
        <Badge key={c} variant="default" size="sm">
          {c}
        </Badge>
      ))}
    </div>
  );
}

function BroadcastRow({
  broadcast,
  canManage,
  busy,
  onEdit,
  onSend,
  onCancel,
  onDelete,
  onTest,
}: {
  broadcast: Broadcast;
  canManage: boolean;
  busy: boolean;
  onEdit: () => void;
  onSend: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onTest: () => void;
}) {
  const isSent = broadcast.status === BroadcastStatus.SENT;
  const isSending = broadcast.status === BroadcastStatus.SENDING;
  const editable = !isSent && !isSending;
  // Delete is allowed in every state EXCEPT while a fan-out is in flight.
  // Deleting a SENT announcement also recalls it from users' inboxes.
  const deletable = !isSending;

  return (
    <tr className="transition-colors hover:bg-surface-secondary">
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-text-primary">{broadcast.title}</p>
        <p className="text-xs text-text-tertiary">
          {broadcast.category === NotificationCategory.MARKETING
            ? 'Marketing'
            : 'Transactional'}{' '}
          · {formatDate(broadcast.createdAt)}
        </p>
      </td>
      <td className="px-4 py-3">
        <ChannelChips broadcast={broadcast} />
      </td>
      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
        {audienceLabel(broadcast.audienceType)}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <Badge variant={STATUS_BADGE[broadcast.status]} size="sm" dot>
          {broadcast.status}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
        {isSent ? (
          <span className="space-x-2">
            {broadcast.inboxEnabled && (
              <span>
                {broadcast.inboxSentCount}/{broadcast.recipientCount} inbox
              </span>
            )}
            {broadcast.emailEnabled && (
              <span>
                {broadcast.sentCount}/{broadcast.recipientCount} email
              </span>
            )}
            {!broadcast.inboxEnabled && !broadcast.emailEnabled && (
              <span className="text-text-tertiary">banner only</span>
            )}
            {broadcast.failedCount > 0 && (
              <span className="text-danger">· {broadcast.failedCount} failed</span>
            )}
          </span>
        ) : (
          <span className="text-text-tertiary">—</span>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right">
        <div className="flex items-center justify-end gap-2">
          {canManage && editable && (
            <Button variant="secondary" size="sm" onClick={onEdit}>
              Edit
            </Button>
          )}
          {canManage && broadcast.emailEnabled && (
            <Button variant="ghost" size="sm" onClick={onTest}>
              Test
            </Button>
          )}
          {canManage && editable && (
            <Button variant="outline" size="sm" onClick={onSend} loading={busy}>
              Send
            </Button>
          )}
          {canManage && editable && broadcast.status !== BroadcastStatus.CANCELLED && (
            <Button variant="ghost" size="sm" onClick={onCancel} loading={busy}>
              Cancel
            </Button>
          )}
          {canManage && deletable && (
            <Button variant="ghost" size="sm" onClick={onDelete} loading={busy}>
              Delete
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
